import React, {useContext, useState} from "react";
import {useNavigate} from "react-router-dom"

import {GroupContext} from "src/pages/context/Group.context";
import {useTranslation} from "react-i18next";
import useFetch, {CachePolicies} from "use-http";
import {HeartFilled, HeartOutlined, LoadingOutlined} from "@ant-design/icons";
import {Button, Spin, Tooltip} from "antd";

import styles from "./GroupCard.module.scss";
import {APIEndpoint} from "src/service/group.service";
import {getUrlHttp} from "src/service/util.service";
import {pathRoutes} from "src/props/util.props";
import earth_grid_symbol from "src/assets/png/earth_grid_symbol.png";
import CenterConfirmModal from "src/components/shared/Modal/CenterConfirmModal";
import _ from "lodash";
import defaultNoimg from "src/assets/png/No_Img.png";

interface GroupCardProps {
  image: string;
  member: number;
  favorite: boolean;
  groupName: string;
  joined: boolean;
  groupID: string;
  mainType: string;
}

const PinAction = {
  PIN: "pin",
  UNPIN: "unpin",
};

const GroupCard = React.forwardRef((props: any, ref: any) => {
  const { t } = useTranslation();
  const { isUsePin=true } = props
  const groupCtx: any = useContext(GroupContext);
  let text = "";
  if (props.requested) {
    text = t("Pending");
  } else {
    if (props.joined) {
      text = props.mainType === "community" ? "Open" : "Chat";
    } else {
      text = "Join";
    }
  }
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState(text);
  const [isClickedJoin, setIsClickedJoin] = useState(props.requested);
  const [isJoined, setIsJoined] = useState(props.joined);
  const [openPinModal, setOpenPinModal] = useState(false);
  const [isPinGroup, setIsPinGroup] = useState(props?.favorite);
  // const buttonText = props.joined ? "Chat" : "join";

  const api = useFetch(window.__RUNTIME_CONFIG__.REACT_APP_PLAYME_URL, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const APIpinGroup = useFetch(
    window.__RUNTIME_CONFIG__.REACT_APP_PLAYME_URL + "/api/v2/groups/pin",
    {
      cachePolicy: CachePolicies.NO_CACHE,
    }
  );

  const APIunPinGroup = useFetch(
    window.__RUNTIME_CONFIG__.REACT_APP_PLAYME_URL + "/api/v2/groups",
    {
      cachePolicy: CachePolicies.NO_CACHE,
    }
  );

  const updateMyGroup = () => {
    const newGroup = groupCtx.publicGroup.find((group: any) => {
      return group?.id === props.groupID;
    });
    newGroup.joined = true;

    const newGroupIndex = groupCtx.publicGroup.findIndex((group: any) => {
      return group?.id === props.groupID;
    });

    const updatedPublicGroup = [...groupCtx.publicGroup];
    updatedPublicGroup.splice(newGroupIndex, 1, newGroup);

    groupCtx.setMyGroup((prev: any) => {
      return [...prev, newGroup];
    });

    groupCtx.setPublicGroup(updatedPublicGroup);
  };

  const updatePublicGroup = (res: any) => {
    const newGroupIndex = groupCtx.publicGroup.findIndex((group: any) => {
      return group?.id === props.groupID;
    });

    const joinStatusArr = new Array();
    joinStatusArr.push(res);

    try {
      const updatedPublicGroup = [...groupCtx.publicGroup];
      updatedPublicGroup[newGroupIndex].joinStatus = joinStatusArr;
    } catch (e) {
      console.log(e);
    }
  };

  const updatePinGroupData = (action: string) => {
    const groupDataRef = isJoined ? groupCtx.myGroup : groupCtx.publicGroup;
    const setGroupDataFunc = isJoined
      ? groupCtx.setMyGroup
      : groupCtx.setPublicGroup;

    if (!_.isEmpty(groupDataRef)) {

      const groupIndex = groupDataRef.findIndex((group: any) => {
        return group?.id === props.groupID;
      });

      const groupData = groupDataRef.find((group: any) => {
        return group?.id === props.groupID;
      });

      groupData.groupPinUser = action === PinAction.PIN ? true : false;

      setGroupDataFunc((prev: any) => {
        prev.splice(groupIndex, 1, groupData);
        return prev;
      });

      groupCtx.setPinGroups((prev: any) => {
        if (action === PinAction.PIN) {
          return [...prev, groupData];
        } else {
          return prev.filter((group: any) => group?.id !== props.groupID);
        }
      });

      groupCtx.setPinGroupsTotal((prev: any) => {
        return action === PinAction.PIN ? prev + 1 : prev - 1;
      });
    }
  };

  const handleClick = async () => {
    setIsLoading(true);
    if (!isJoined) {
      const res = await api.post(APIEndpoint.request, {
        groupID: props.groupID,
      });
      if (api?.response?.ok) {
        if (res?.status === "done") {
          setButtonText("Chat");
          setIsJoined(true);
          updateMyGroup();
        } else {
          setButtonText("Pending");
          setIsClickedJoin(true);
          updatePublicGroup(res);
        }
        setIsLoading(false);
      }
    } else {
      if (buttonText === "Chat") {
        setTimeout(() => {
          navigate(pathRoutes.Chat + "?id=" + props.groupID);
        }, 1000);
      } else {
        setTimeout(() => {
          navigate(`${pathRoutes.groupCommunity}/${props.groupID}`);
        }, 1000);
      }
    }
  };

  const checkCommunity = () => {
    if (props.mainType === "community") {
      return true;
    } else {
      return false;
    }
  };

  const getPinModalText = () => {
    return isPinGroup
      ? t("Are you sure to remove favorite this group")
      : t("Are you sure to save favorite this group");
  };

  const handlePinGroup = async () => {
    await APIpinGroup.post({ groupID: props?.groupID });

    if (APIpinGroup.response.ok) {
      console.log(`pin group: ${props.groupName}`);
    }
  };

  const handleUnPinGroup = async () => {
    await APIunPinGroup.delete(`/pin-delete/${props?.groupID}`);

    if (APIunPinGroup.response.ok) {
      console.log(`unpin group: ${props.groupName}`);
    }
  };

  const handleClickPinGroup = async () => {
    try {
      if (isPinGroup) {
        await handleUnPinGroup();
        updatePinGroupData(PinAction.UNPIN);
        setTimeout(() => {
          setIsPinGroup(false);
        }, 400);
      } else {
        await handlePinGroup();
        updatePinGroupData(PinAction.PIN);
        setTimeout(() => {
          setIsPinGroup(true);
        }, 400);
      }
      setOpenPinModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{
      maxWidth: "200px",
      height: "200px",
      marginBottom: "20px"
    }}>
      <div
        ref={ref}
        className={styles.card}
        style={{
          backgroundImage: `url(${getUrlHttp(props.image)?props.image: defaultNoimg})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <CenterConfirmModal
          visible={openPinModal}
          onCancel={() => {
            setOpenPinModal(false);
          }}
          onConfirm={handleClickPinGroup}
          text={getPinModalText()}
          confirmText={t("OK")}
          cancelText={t("Cancel")}
        />
        {checkCommunity() && (
          <div className={styles.boxGroup}>
            <img src={earth_grid_symbol} width={17} height={17}></img>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {(isJoined && isUsePin) && (
            <>
              {isPinGroup ? (
                <HeartFilled
                  className={styles.heartIcon}
                  onClick={() => {
                    setOpenPinModal(true);
                  }}
                />
              ) : (
                <HeartOutlined
                  className={styles.heartIcon}
                  onClick={() => {
                    setOpenPinModal(true);
                  }}
                />
              )}
            </>
          )}
        </div>

      </div>
      <div style={{ display: "flex", flexDirection: "column", paddingTop: "12px" }}>

        <div
          style={{
            display: "flex",
          }}
        >
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            <Tooltip placement="topLeft" title={props.groupName} color="#0f4c82">
              <div className={styles.headCardDetail}>{props.groupName}</div>
            </Tooltip>
            <div
              className={styles.cardDetail}
            >{`(${props.member} ${t("people")})`}</div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center"
            }}
          >
            <Button
              className={
                props.requested || isClickedJoin
                  ? styles.pendingButton
                  : styles.chatButton
              }
              style={{
                pointerEvents: isLoading || isClickedJoin ? "none" : "auto",
              }}
              onClick={handleClick}
            >
              {!isLoading ? (
                t(buttonText)
              ) : (
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: "20px", color: "#0F4C82" }}
                    />
                  }
                />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>


  );
});

export default GroupCard;
